/**
 * Helpers to group the copilot model catalog by provider (company).
 *
 * The catalog returned by `GET /api/copilot/models` uses namespaced ids in the
 * form `provider/model` (e.g. `openai/gpt-4o-mini`). The provider/company is
 * derived purely from that prefix — no extra backend call is needed. Pricing /
 * credits are intentionally ignored: only provider and model are exposed.
 */

export interface CopilotModelOption {
  id: string;
  name: string;
  /** A free model of the gateway's catalog (RF-53); listed in the Free group only. */
  free?: boolean;
}

export interface CopilotProviderGroup {
  slug: string;
  label: string;
  /** An i18n key for a group whose name is translated rather than a brand (the Free group). */
  labelKey?: string;
  models: CopilotModelOption[];
}

/** The group of the free models, listed after every provider (D-13). */
export const FREE_PROVIDER_SLUG = 'free';
const FREE_PROVIDER_LABEL_KEY = 'channel.copilotModelSelectDialog.freeProvider';

/** Friendly company names for the known provider slugs across both gateways. */
const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  deepseek: 'Deepseek',
  meta: 'Meta',
  'meta-llama': 'Meta',
  'x-ai': 'xAI',
  xai: 'xAI',
  qwen: 'Alibaba',
  alibaba: 'Alibaba',
  maritaca: 'Maritaca',
  mistral: 'Mistral',
  mistralai: 'Mistral',
  cohere: 'Cohere',
  perplexity: 'Perplexity',
  selfhosted: 'Self-hosted',
};

/** Display order for providers; unknown providers fall to the end (alphabetical). */
const PROVIDER_ORDER = [
  'openai',
  'anthropic',
  'google',
  'meta',
  'meta-llama',
  'deepseek',
  'x-ai',
  'xai',
  'qwen',
  'alibaba',
  'mistral',
  'mistralai',
  'maritaca',
  'cohere',
  'perplexity',
];

/** The provider slug of a model id (segment before the first `/`). */
export function providerSlugOf(id: string): string {
  const i = (id ?? '').indexOf('/');
  return i > 0 ? id.slice(0, i).toLowerCase() : 'selfhosted';
}

/** The friendly company label for a provider slug. */
export function providerLabel(slug: string): string {
  if (PROVIDER_LABELS[slug]) return PROVIDER_LABELS[slug];
  if (!slug) return '';
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

/** Normalizes gateway-specific slugs to a single logo filename. */
function logoSlug(slug: string): string {
  switch (slug) {
    case 'x-ai':
      return 'xai';
    case 'meta-llama':
      return 'meta';
    case 'mistralai':
      return 'mistral';
    case 'alibaba':
      return 'qwen';
    default:
      return slug;
  }
}

/**
 * Asset path of the provider's official logo, vendored under
 * `assets/images/llm-providers/<slug>.svg` (sourced from the lobehub icon set).
 * Drop a new `<slug>.svg` there and it resolves automatically; when a file is
 * absent the template's `(error)` handler falls back to a neutral placeholder.
 */
export function providerLogo(slug: string): string {
  return `assets/images/llm-providers/${logoSlug(slug)}.svg`;
}

/**
 * The model's display name without the redundant "Provider: " prefix that some
 * catalogs include (e.g. "OpenAI: GPT-4o mini" → "GPT-4o mini").
 */
export function modelLabel(model: CopilotModelOption): string {
  const name = (model?.name ?? '').trim() || model?.id || '';
  const colon = name.indexOf(': ');
  if (colon > 0 && colon <= 24) {
    return name.slice(colon + 2).trim() || name;
  }
  return name;
}

function orderIndex(slug: string): number {
  if (slug === FREE_PROVIDER_SLUG) return PROVIDER_ORDER.length + 1;
  const i = PROVIDER_ORDER.indexOf(slug);
  return i === -1 ? PROVIDER_ORDER.length : i;
}

function groupSlugOf(model: CopilotModelOption): string {
  return model.free ? FREE_PROVIDER_SLUG : providerSlugOf(model.id);
}

function newGroup(slug: string): CopilotProviderGroup {
  if (slug === FREE_PROVIDER_SLUG) return { slug, label: '', labelKey: FREE_PROVIDER_LABEL_KEY, models: [] };
  return { slug, label: providerLabel(slug), models: [] };
}

/** Groups the flat model catalog into provider groups, ordered for display; the free models form the last group. */
export function groupModelsByProvider(models: CopilotModelOption[]): CopilotProviderGroup[] {
  const map = new Map<string, CopilotProviderGroup>();
  for (const model of models ?? []) {
    if (!model?.id) continue;
    const slug = groupSlugOf(model);
    let group = map.get(slug);
    if (!group) {
      group = newGroup(slug);
      map.set(slug, group);
    }
    group.models.push(model);
  }
  const groups = Array.from(map.values());
  groups.forEach((g) => g.models.sort((a, b) => modelLabel(a).localeCompare(modelLabel(b))));
  groups.sort((a, b) => orderIndex(a.slug) - orderIndex(b.slug) || a.label.localeCompare(b.label));
  return groups;
}

/** The group that holds a model, free or not. */
export function providerGroupOf(groups: CopilotProviderGroup[], modelId: string): CopilotProviderGroup | undefined {
  return groups.find((group) => group.models.some((model) => model.id === modelId));
}
